import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import proj4 from "proj4";
import { toast } from "sonner";
import { Map as MapIcon, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { listFiles, getFile } from "@/lib/api";

const ZONES = [54, 55, 56];
const mgaDef = (z) => `+proj=utm +zone=${z} +south +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs`;

const BASEMAPS = [
  { id: "street", label: "Street", url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", opts: { maxZoom: 19, subdomains: "abc" } },
  { id: "satellite", label: "Satellite", url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", opts: { maxZoom: 19 } },
  { id: "contours", label: "Contours", url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png", opts: { maxZoom: 17, subdomains: "abc" } },
  { id: "hillshade", label: "Hillshade", url: "https://server.arcgisonline.com/ArcGIS/rest/services/Elevation/World_Hillshade/MapServer/tile/{z}/{y}/{x}", opts: { maxZoom: 16 } },
];

const CLASS_COLORS = {
  Stormwater: "#2563eb",
  Sewerage: "#7c3aed",
  Water: "#059669",
  Cadastre: "#d97706",
  OpenSpace: "#16a34a",
  Transport: "#db2777",
  Surface: "#6b7280",
  Unclassified: "#52525b",
};

function classOf(asset) {
  const segs = (asset.path || "").split("/").map((s) => s.toLowerCase().replace(/\s/g, ""));
  const map = { stormwater: "Stormwater", sewerage: "Sewerage", water: "Water", watersupply: "Water", cadastre: "Cadastre", openspace: "OpenSpace", transport: "Transport", surface: "Surface", supplementary: "Unclassified" };
  for (const s of segs) if (map[s]) return map[s];
  return "Unclassified";
}

export default function MapPage() {
  const [files, setFiles] = useState([]);
  const [fileId, setFileId] = useState("");
  const [zone, setZone] = useState(56);
  const [basemap, setBasemap] = useState("street");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const layerRef = useRef(null);
  const baseRef = useRef(null);

  useEffect(() => {
    listFiles().then(setFiles);
  }, []);

  useEffect(() => {
    if (mapObj.current || !mapRef.current) return;
    const map = L.map(mapRef.current, { zoomControl: true, attributionControl: false }).setView([-24.87, 152.35], 13);
    const def = BASEMAPS.find((b) => b.id === "street");
    baseRef.current = L.tileLayer(def.url, def.opts).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapObj.current = map;
    setTimeout(() => map.invalidateSize(), 200);
  }, []);

  // swap basemap
  useEffect(() => {
    if (!mapObj.current) return;
    const def = BASEMAPS.find((b) => b.id === basemap);
    if (baseRef.current) mapObj.current.removeLayer(baseRef.current);
    baseRef.current = L.tileLayer(def.url, def.opts).addTo(mapObj.current);
    baseRef.current.bringToBack();
  }, [basemap]);

  const render = async (id, z) => {
    if (!id || !mapObj.current) return;
    setLoading(true);
    try {
      const doc = await getFile(id);
      layerRef.current.clearLayers();
      const def = mgaDef(z);
      const pts = [];
      let mapped = 0;
      const classes = {};
      const reproj = (e, n) => {
        const [lon, lat] = proj4(def, proj4.WGS84, [e, n]);
        return [lat, lon];
      };
      doc.assets.forEach((a) => {
        // gather plan coordinates: explicit Easting_m/Northing_m fields, else parsed <Geometry> vertices
        let verts = [];
        const fe = parseFloat(a.fields.Easting_m);
        const fn = parseFloat(a.fields.Northing_m);
        if (!isNaN(fe) && !isNaN(fn)) {
          verts = [[fe, fn]];
        } else if (Array.isArray(a.coords) && a.coords.length) {
          verts = a.coords.filter((c) => c[0] != null && c[1] != null).map((c) => [c[0], c[1]]);
        }
        if (!verts.length) return;
        const cls = classOf(a);
        const color = CLASS_COLORS[cls] || CLASS_COLORS.Unclassified;
        classes[cls] = (classes[cls] || 0) + 1;
        const rows = Object.entries(a.fields)
          .filter(([k]) => !["Easting_m", "Northing_m", "Geometry"].includes(k))
          .slice(0, 12)
          .map(([k, v]) => `<tr><td style="color:#71717a;padding-right:8px">${k}</td><td style="color:#18181b">${v}</td></tr>`)
          .join("");
        const popupHtml = `<div style="font-family:'JetBrains Mono',monospace;font-size:11px;min-width:200px">
            <div style="color:${color};font-weight:700;margin-bottom:4px">${a.asset_id} · ${cls}</div>
            <div style="color:#71717a;margin-bottom:6px">${a.type}</div>
            <table>${rows}</table>
          </div>`;
        const latlngs = verts.map(([e, n]) => reproj(e, n));
        if (latlngs.length >= 2) {
          const line = L.polyline(latlngs, { color, weight: 3, opacity: 0.9 });
          line.bindPopup(popupHtml);
          line.addTo(layerRef.current);
          const mid = latlngs[Math.floor(latlngs.length / 2)];
          const m = L.circleMarker(mid, { radius: 5, color: "#ffffff", weight: 1.5, fillColor: color, fillOpacity: 0.95 });
          m.bindPopup(popupHtml);
          m.addTo(layerRef.current);
          latlngs.forEach((ll) => pts.push(ll));
        } else {
          const m = L.circleMarker(latlngs[0], { radius: 7, color: "#ffffff", weight: 2, fillColor: color, fillOpacity: 0.95 });
          m.bindPopup(popupHtml);
          m.addTo(layerRef.current);
          pts.push(latlngs[0]);
        }
        mapped++;
      });
      if (pts.length) mapObj.current.fitBounds(pts, { padding: [60, 60], maxZoom: 17 });
      setStats({ total: doc.assets.length, mapped, classes });
      if (mapped === 0) toast.info("No mappable assets — file has no coordinates (Easting_m/Northing_m or <Geometry> vertices). If this is a newly-uploaded real ADAC file, re-upload it after the latest deploy so geometry is parsed.");
    } catch (err) {
      toast.error("Could not load file");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (fileId) render(fileId, zone);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId, zone]);

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center justify-between px-8 sticky top-0 bg-[#0A0A0A]/85 backdrop-blur z-[1100]">
        <div>
          <h1 className="font-display text-xl font-bold tracking-tight">Map</h1>
          <p className="font-mono text-[11px] text-zinc-500">Spatial view — MGA / GDA2020 → WGS84 reprojection</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(zone)} onValueChange={(v) => setZone(parseInt(v))}>
            <SelectTrigger data-testid="zone-select" className="w-32 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm">
              {ZONES.map((z) => (
                <SelectItem key={z} value={String(z)} className="font-mono text-xs">MGA Zone {z}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={fileId} onValueChange={setFileId}>
            <SelectTrigger data-testid="map-file-select" className="w-64 rounded-sm bg-[#0A0A0A] border-zinc-700 font-mono text-sm">
              <SelectValue placeholder="Select a document…" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-zinc-800 rounded-sm">
              {files.map((f) => (
                <SelectItem key={f.id} value={f.id} className="font-mono text-xs">{f.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </header>

      <div className="p-8">
        <div className="relative border border-zinc-800 rounded-sm overflow-hidden">
          <div ref={mapRef} data-testid="map-canvas" style={{ height: "70vh", background: "#e5e7eb" }} />

          {/* Basemap switcher */}
          <div className="absolute top-3 right-3 z-[1000] flex rounded-sm overflow-hidden border border-zinc-700 bg-[#121212]/95 backdrop-blur" data-testid="basemap-switcher">
            {BASEMAPS.map((b) => (
              <button
                key={b.id}
                data-testid={`basemap-${b.id}`}
                onClick={() => setBasemap(b.id)}
                className={`px-3 py-1.5 font-mono text-[11px] transition-colors duration-75 ${
                  basemap === b.id ? "bg-blue-600 text-white" : "text-zinc-400 hover:bg-zinc-800"
                }`}
              >
                {b.label}
              </button>
            ))}
          </div>

          {loading && (
            <div className="absolute top-3 left-3 z-[1000] flex items-center gap-2 font-mono text-[11px] text-zinc-300 bg-[#121212]/90 border border-zinc-800 rounded-sm px-3 py-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Reprojecting…
            </div>
          )}
          {stats && (
            <div className="absolute bottom-3 left-3 z-[1000] bg-[#121212]/95 border border-zinc-800 rounded-sm p-3" data-testid="map-legend">
              <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
                {stats.mapped} / {stats.total} mapped
              </div>
              <div className="space-y-1">
                {Object.entries(stats.classes).map(([c, n]) => (
                  <div key={c} className="flex items-center gap-2 font-mono text-[11px]">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: CLASS_COLORS[c] || CLASS_COLORS.Unclassified }} />
                    <span className="text-zinc-300">{c}</span>
                    <span className="text-zinc-600">{n}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!fileId && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none z-[500]">
              <MapIcon className="w-10 h-10 text-zinc-700 mb-3" />
              <p className="text-sm text-zinc-500">Select a document to plot its assets on the map.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
